import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import { Grid, Typography, Box } from '@mui/material';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import { gridSpacing } from 'utils/constants';
import windowsImg from 'assets/images/windows.png';
import windowsMSIImg from 'assets/images/windows_msi.png';
import macOSImg from 'assets/images/macOS.png';
import applianceImg from 'assets/images/appliance.png';
import txtImg from 'assets/images/txt.png';
import sccmImg from 'assets/images/sccm.png';
import pdfImg from 'assets/images/pdf.png';
import docImg from 'assets/images/doc.png';
import DownloadFileApiHelper from 'utils/downloadFileApiHelper';
import ApiHelper from 'utils/apiHelper';

const ManagementReference = () => {
    const theme = useTheme();
    const { userInfo } = useSelector((state) => state.auth);
    const toastId = useRef(null);
    const manageOrganizationData = JSON.parse(localStorage.getItem('manageOrganization'));
    const [pluginData, setPluginData] = useState({
        appliance: false,
        sccm: false
    });

    useEffect(() => {
        getPluginReference();
    }, []);

    const getPluginReference = () => {
        ApiHelper.get('/api/management/check_reference_plugin/')
            .then((res) => setPluginData(res.data))
            .catch();
    };

    const handleDownloadFile = (e) => {
        toastId.current = toast.loading('Downloading, please wait...');
        DownloadFileApiHelper.post(
            '/api/management/download_reference_file/',
            {
                data: { file_name: e.target.name },
                responseType: 'blob'
            },
            {}
        )
            .then((response) => {
                let url = window.URL.createObjectURL(new Blob([response.data]));
                let filename;
                let a = document.createElement('a');
                a.href = url;
                filename = response.headers['content-disposition'].split('filename=')[1];
                a.download = filename;
                a.click();
                toast.update(toastId.current, {
                    render: 'Successfully downloaded',
                    type: 'success',
                    isLoading: false,
                    autoClose: 5000
                });
            })
            .catch((err) => {
                let content;
                if (err.response.status === 400) {
                    content = 'Failed! Could not find this file';
                    toast.error(``);
                } else {
                    content = 'Failed to download the file';
                }
                toast.update(toastId.current, {
                    render: content,
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000
                });
            });
    };

    return (
        <MainCard
            title="Reference"
            cardHeaderTitle={manageOrganizationData?.orgid ? manageOrganizationData.name : userInfo.user.orgid.name}
            cardHeaderShow={true}
        >
            <Grid container spacing={gridSpacing}>
                <Grid item xs={12}>
                    <SubCard title="Installer Downloads">
                        <Grid container>
                            <Grid item xs={2} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={windowsImg} alt="Windows" name="windows" width="80" />
                                    <Typography>EXE</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={windowsMSIImg} alt="Windows MSI" name="windows_msi" width="80" />
                                    <Typography>MSI</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={macOSImg} alt="macOs" name="macos" width="80" />
                                    <Typography>PKG</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={txtImg} alt="Chrome" name="chromebook" width="55" />
                                    <Typography>Chrome</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={12}>
                    <SubCard title="Plug-in Downloads">
                        <Grid container>
                            {pluginData.sccm && (
                                <Grid item xs={2} sx={{ textAlign: 'center' }}>
                                    <Box onClick={handleDownloadFile} className="reference">
                                        <img src={sccmImg} alt="sccm" name="sccm_plugin" width="70" />
                                    </Box>
                                </Grid>
                            )}
                            {pluginData.appliance && (
                                <Grid item xs={2} sx={{ textAlign: 'center' }}>
                                    <Box onClick={handleDownloadFile} className="reference">
                                        <img src={applianceImg} alt="appliance" name="appliance" width="70" />
                                    </Box>
                                </Grid>
                            )}
                            {pluginData.appliance === false && pluginData.sccm === false && (
                                <Typography color={theme.palette.secondary.main}>There is no plug-in data for this organization</Typography>
                            )}
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={12}>
                    <SubCard title="Documentation">
                        <Grid container>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="Tech Specs" name="tech_specs" width="55" />
                                    <Typography>TechSpecs</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="welcome" name="welcome" width="55" />
                                    <Typography>Welcome!</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="Tech Specs" name="security" width="55" />
                                    <Typography>Security</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={docImg} alt="User Kit" name="user_kit" width="55" />
                                    <Typography>User Kit</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="EULA" name="eula" width="55" />
                                    <Typography>EULA</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="SLA" name="sla" width="55" />
                                    <Typography>SLA</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="Admin Guide" name="admin_guide" width="55" />
                                    <Typography>Admin Guide</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="Install Guide" name="install_guide" width="55" />
                                    <Typography>Install Guide</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={txtImg} alt="Release Notes" name="release_notes" width="55" />
                                    <Typography>Release Notes</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="Using JAMF" name="jamfguide" width="55" />
                                    <Typography>Using JAMF</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'center' }}>
                                <Box onClick={handleDownloadFile} className="reference">
                                    <img src={pdfImg} alt="SCCM Guide" name="sccm_guide" width="55" />
                                    <Typography>SCCM Guide</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ManagementReference;
